import { Box, CircularProgress } from '@mui/material';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useMe } from './features/auth/useMe';
import { LoginPage } from './features/auth/LoginPage';
import { AppShell } from './components/AppShell';
import { NAV_GROUPS } from './components/nav';
import { PreferencesPage } from './pages/PreferencesPage';
import { ActivityHistoryPage } from './pages/ActivityHistoryPage';
import { AgentPage } from './pages/AgentPage';
import { AgentsPage } from './pages/AgentsPage';
import { FinancePage } from './pages/FinancePage';
import { CarrierPage } from './pages/CarrierPage';
import { CarriersPage } from './pages/CarriersPage';
import { CustomerPage } from './pages/CustomerPage';
import { CustomersPage } from './pages/CustomersPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { CommunicationsPage } from './pages/CommunicationsPage';
import { ConversationPage } from './pages/ConversationPage';
import { NewShipmentPage } from './pages/NewShipmentPage';
import { NewQuotationPage } from './pages/NewQuotationPage';
import { QuotationsPage } from './pages/QuotationsPage';
import { ShipmentPage } from './pages/ShipmentPage';
import { ShipmentsPage } from './pages/ShipmentsPage';
import { ApprovalsPage } from './pages/ApprovalsPage';
import { HomePage } from './pages/HomePage';
import { ComingSoonPage } from './pages/SectionPage';
import { RemindersPage } from './pages/RemindersPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { DevicesPage } from './pages/SettingsPages';

// pages that exist; every other menu item shows "coming soon"
const BUILT: Record<string, () => React.ReactElement> = {
  '/approvals': ApprovalsPage,
  '/shipments': ShipmentsPage,
  '/quotations': QuotationsPage,
  '/documents': DocumentsPage,
  '/customers': CustomersPage,
  '/agents': AgentsPage,
  '/carriers': CarriersPage,
  '/finance': FinancePage,
  '/history': ActivityHistoryPage,
  '/preferences': PreferencesPage,
  '/communications': CommunicationsPage,
  '/reminders': RemindersPage,
  '/notifications': NotificationsPage,
  '/devices': DevicesPage,
};
const COMING_SOON = NAV_GROUPS.flatMap((g) => g.items).filter((i) => i.to !== '/' && !BUILT[i.to]);

/** Signed in → the app; not signed in → the login page */
export function App() {
  const me = useMe();
  if (me.isPending)
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100dvh' }}>
        <CircularProgress />
      </Box>
    );
  if (!me.data) return <LoginPage />;
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AppShell me={me.data} />}>
          <Route index element={<HomePage />} />
          <Route path="/search" element={<ComingSoonPage title="Search" />} />
          <Route path="/approvals/:id" element={<ApprovalsPage />} />
          <Route path="/shipments/new" element={<NewShipmentPage />} />
          <Route path="/shipments/:srb" element={<ShipmentPage />} />
          <Route path="/communications/:srb" element={<ConversationPage />} />
          <Route path="/history/:id" element={<ActivityHistoryPage />} />
          <Route path="/carriers/:id" element={<CarrierPage />} />
          <Route path="/agents/:id" element={<AgentPage />} />
          <Route path="/customers/:id" element={<CustomerPage />} />
          <Route path="/quotations/new" element={<NewQuotationPage />} />
          <Route path="/quotations/:srb" element={<QuotationsPage />} />
          {Object.entries(BUILT).map(([path, Page]) => (
            <Route key={path} path={path} element={<Page />} />
          ))}
          {COMING_SOON.map((i) => (
            <Route key={i.to} path={i.to} element={<ComingSoonPage title={i.label} />} />
          ))}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
